/**
 * Placeholder values store — a tiny client-side pub/sub for the live PDF
 * overlay (FieldMarker) and the orchestrator chat path.
 *
 * Why module-level state instead of React Context: the same value can be set
 * from many places (chat tool result, pre-sign warning, inline edit popover),
 * none of which share an obvious provider tree. A single subscribable store
 * keeps everything in sync without prop-drilling, and reads are O(1).
 *
 * Keys are namespaced by documentId so multiple PDFs in the workspace don't
 * collide.
 *
 * NOTE: Values here are EPHEMERAL — they live in memory until the user saves
 * or sends. The "save" path bakes them into the actual PDF binary by calling
 * fill_pdf_form on the backend.
 */
import { useCallback, useEffect, useState } from 'react';

export type PlaceholderValueSource = 'user' | 'ai' | 'profile';

export interface PlaceholderValue {
  value: string;
  source: PlaceholderValueSource;
  /** When the value was set. Used for last-write-wins resolution. */
  setAt: number;
}

type Listener = (snapshot: Record<string, PlaceholderValue>) => void;

interface DocumentSlice {
  values: Record<string, PlaceholderValue>;
  listeners: Set<Listener>;
}

const slices = new Map<string, DocumentSlice>();

function getSlice(documentId: string): DocumentSlice {
  let slice = slices.get(documentId);
  if (!slice) {
    slice = { values: {}, listeners: new Set() };
    slices.set(documentId, slice);
  }
  return slice;
}

function notify(slice: DocumentSlice) {
  for (const listener of slice.listeners) {
    listener(slice.values);
  }
}

export function setPlaceholderValue(
  documentId: string,
  fieldId: string,
  value: string,
  source: PlaceholderValueSource = 'user',
): void {
  const slice = getSlice(documentId);
  slice.values = {
    ...slice.values,
    [fieldId]: { value, source, setAt: Date.now() },
  };
  notify(slice);
}

export function clearPlaceholderValue(documentId: string, fieldId: string): void {
  const slice = getSlice(documentId);
  if (!slice.values[fieldId]) return;
  const next = { ...slice.values };
  delete next[fieldId];
  slice.values = next;
  notify(slice);
}

export function setManyPlaceholderValues(
  documentId: string,
  values: Record<string, string>,
  source: PlaceholderValueSource = 'ai',
): void {
  const slice = getSlice(documentId);
  const setAt = Date.now();
  const next = { ...slice.values };
  for (const [fieldId, value] of Object.entries(values)) {
    next[fieldId] = { value, source, setAt };
  }
  slice.values = next;
  notify(slice);
}

export function getPlaceholderValues(documentId: string): Record<string, PlaceholderValue> {
  return getSlice(documentId).values;
}

export function getPlaceholderValue(documentId: string, fieldId: string): PlaceholderValue | undefined {
  return getSlice(documentId).values[fieldId];
}

export function clearDocumentPlaceholders(documentId: string): void {
  const slice = slices.get(documentId);
  if (!slice) return;
  slice.values = {};
  notify(slice);
}

/**
 * Subscribe to changes for a document slice. Returns the current snapshot and
 * re-renders the consumer whenever any value in the slice changes.
 */
export function usePlaceholderValues(documentId: string | null | undefined): {
  values: Record<string, PlaceholderValue>;
  setValue: (fieldId: string, value: string, source?: PlaceholderValueSource) => void;
  clearValue: (fieldId: string) => void;
} {
  const [snapshot, setSnapshot] = useState<Record<string, PlaceholderValue>>(() =>
    documentId ? getSlice(documentId).values : {},
  );

  useEffect(() => {
    if (!documentId) {
      setSnapshot({});
      return;
    }
    const slice = getSlice(documentId);
    setSnapshot(slice.values);
    const listener: Listener = (next) => setSnapshot(next);
    slice.listeners.add(listener);
    return () => {
      slice.listeners.delete(listener);
    };
  }, [documentId]);

  const setValue = useCallback(
    (fieldId: string, value: string, source: PlaceholderValueSource = 'user') => {
      if (!documentId) return;
      setPlaceholderValue(documentId, fieldId, value, source);
    },
    [documentId],
  );

  const clearValue = useCallback(
    (fieldId: string) => {
      if (!documentId) return;
      clearPlaceholderValue(documentId, fieldId);
    },
    [documentId],
  );

  return { values: snapshot, setValue, clearValue };
}

/**
 * Snapshot helper for the save-to-PDF path. Returns a plain map suitable for
 * the fill_pdf_form values param.
 */
export function snapshotForBackend(documentId: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [fieldId, entry] of Object.entries(getSlice(documentId).values)) {
    out[fieldId] = entry.value;
  }
  return out;
}
